import { useState, useEffect, useCallback } from 'react';
import { User } from 'firebase/auth';
import { workspaceService, WorkspaceWithRole } from '../services/workspace.service';
import { UserData } from '../contexts/AuthContext';

interface UseWorkspaceManagerProps {
  user: User | null;
  userProfile: UserData | null;
}

interface UseWorkspaceManagerReturn {
  availableWorkspaces: WorkspaceWithRole[];
  currentWorkspace: WorkspaceWithRole | null;
  loading: boolean;
  error: string | null;
  refreshWorkspaces: () => Promise<void>;
  switchWorkspace: (workspaceId: string) => Promise<void>;
}

export const useWorkspaceManager = ({
  user,
  userProfile,
}: UseWorkspaceManagerProps): UseWorkspaceManagerReturn => {
  const [availableWorkspaces, setAvailableWorkspaces] = useState<WorkspaceWithRole[]>([]);
  // Initial value MUST be true so consumers (e.g. HomePage) render their
  // loading state on first mount instead of treating "no workspaces yet" as
  // "user has zero workspaces" and redirecting to /welcome.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | null>(
    workspaceService.getCurrentWorkspaceId()
  );

  const currentWorkspace = availableWorkspaces.find((w) => w.id === currentWorkspaceId) || null;

  // Refresh workspaces from server. Stays in `loading=true` until both
  // `user` and `userProfile` are settled — otherwise consumers (HomePage)
  // would briefly see `loading=false, availableCount=0` during the initial
  // Firebase auth restore and redirect to /welcome by mistake.
  const refreshWorkspaces = useCallback(async () => {
    if (!user || !userProfile) {
      // Auth still resolving — keep `loading=true`, don't clear the list
      // because that would flash empty state to consumers.
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const idToken = await user.getIdToken();
      const workspaces = await workspaceService.getUserWorkspaces(idToken);
      setAvailableWorkspaces(workspaces);
    } catch (err) {
      setError('Failed to load workspaces');
      setAvailableWorkspaces([]);
    } finally {
      setLoading(false);
    }
  }, [user, userProfile]);

  // Switch workspace (client-side only)
  const switchWorkspace = useCallback(async (workspaceId: string) => {
    try {
      workspaceService.switchWorkspace(workspaceId);
      setCurrentWorkspaceId(workspaceId);
    } catch (err) {
      console.error('Failed to switch workspace:', err);
      setError('Failed to switch workspace');
      throw err;
    }
  }, []);

  // Load workspaces when user changes
  useEffect(() => {
    refreshWorkspaces();
  }, [refreshWorkspaces]);

  // Auto-select first workspace if none is currently selected or if the
  // persisted current workspace is no longer in the user's list.
  useEffect(() => {
    if (availableWorkspaces.length > 0) {
      const currentExists =
        currentWorkspaceId && availableWorkspaces.find((w) => w.id === currentWorkspaceId);

      if (!currentExists) {
        switchWorkspace(availableWorkspaces[0].id);
      }
    }
  }, [availableWorkspaces, currentWorkspaceId, switchWorkspace]);

  return {
    availableWorkspaces,
    currentWorkspace,
    loading,
    error,
    refreshWorkspaces,
    switchWorkspace,
  };
};
